// Every PostgreSQL enum type from `backend/db/schema.sql`, as a const object plus
// a union type. The strings are values in a system of record: they drive the
// status machine, the authority checks and the Point Click Care CSV, so an
// abbreviation is a wrong value, not a convenience. Reproduce them exactly.
//
// Casing splits are deliberate and load-bearing for parsers: `decision_step` is
// Title Case, `decision_outcome` is lower case, `assignment_request_status` is
// capitalized, `assignment_role` is lower-hyphenated.
//
// DELIBERATE EXCEPTIONS: RETIRED SQL VALUES. Postgres 16 has no syntax to drop
// an enum value (no `ALTER TYPE … DROP VALUE`), so `schema.sql` keeps a few
// labels that nothing writes any more, and they are deliberately absent from
// the live lists below:
//
//  * `invoice_status` `'Matching Failed'` — retired by question 4.11
//    (2026-08-12). A matching failure lands at `Pending Import Control
//    Decision` carrying a `MatchingFailed` flag instead
//    (`backend/src/domain/matchingLadder.ts`'s `matchingFailedWrites`).
//  * `invoice_status` `'Pending AP/AR Approval Decision'` and `decision_step`
//    `'AP/AR Approval Decision'` — retired when AP/AR Approval was merged into
//    Import Control (owner, 2026-09-29 — `docs/decisions.md` D-215).
//    `backend/db/post-schema.sql` moves every invoice still at the old status to
//    `Pending Import Control Decision`, so no live row carries it. Old DECISION
//    and CORRECTION rows still carry the step, which is why
//    `RETIRED_DECISION_STEP` below names it for the readers that show history.
//  * `invoice_status` `'Pending Manual Entry Decision'` and `decision_step`
//    `'Manual Entry Decision'` — retired when the Manual Entry step was dropped
//    (owner, 2026-10-07 — `docs/decisions.md` D-311). Export Control's
//    **Typed Into Point Click Care** now ends the invoice there.
//    Nothing moves an invoice still at the old status (D-302: a deploy edits no
//    records); the owner ruled that no concern, since dev processes no real
//    invoices (D-315). Old decision
//    rows still carry the step, so `RETIRED_DECISION_STEP` names it too.
//
// RETIRED ROLES ARE NOT NAMED HERE AT ALL (owner, 2026-10-04 — D-293: they "do
// not exist ... there is no reason even to keep these old roles in the code").
// The database enums `user_role` and `assignment_role` still carry them; the
// list is in `docs/schema.md`. A stored row with a role the code does not know
// is invisible to the repository and grants nothing at sign-in.
//
// `schema.sql`'s `CREATE TYPE` blocks carry the same notes from the other side.

/** The workspace discriminator. One value at launch; claims are additive later. */
export const WORKSPACE_KIND = {
  invoice: 'invoice',
} as const
export type WorkspaceKind = (typeof WORKSPACE_KIND)[keyof typeof WORKSPACE_KIND]

/**
 * Which system a record came from (owner, 2026-10-19 — D-380). Every invoice
 * this app reads in itself is `accounts_payable`. `fleet_maintenance` is
 * `PACE Healthcare Transportation`'s own Fleet Maintenance app, whose finalized
 * invoices are copied in with their line items (`docs/fleet-maintenance.md`).
 *
 * Stored on `invoices.source_system` and `invoice_line_items.source_system`,
 * beside `source_record_id` — the record's id in that system, and `null`
 * exactly when the source is `accounts_payable`. A further source is one more
 * value here, never another column.
 *
 * A record from any source but `accounts_payable` is LOCKED: the copy must
 * keep agreeing with the original, so only Vendor, Account and Department can
 * change (`isLockedSource`, `backend/src/domain/sourceLock.ts`).
 */
export const SOURCE_SYSTEM = {
  accounts_payable: 'accounts_payable',
  fleet_maintenance: 'fleet_maintenance',
} as const
export type SourceSystem = (typeof SOURCE_SYSTEM)[keyof typeof SOURCE_SYSTEM]

export const SOURCE_SYSTEMS: readonly SourceSystem[] = Object.values(SOURCE_SYSTEM)

/** The name a person reads. Title Case: each is the name of a system. */
export const SOURCE_SYSTEM_LABEL: Readonly<Record<SourceSystem, string>> = {
  accounts_payable: 'Accounts Payable',
  fleet_maintenance: 'Fleet Maintenance',
}

/** True for a string the code knows as a source system. */
export function isSourceSystem(value: string): value is SourceSystem {
  return (SOURCE_SYSTEMS as readonly string[]).includes(value)
}

/** True when a record's values belong to another system and may not be edited here. */
export function isLockedSource(source: SourceSystem | null | undefined): boolean {
  return source != null && source !== SOURCE_SYSTEM.accounts_payable
}

/**
 * Pipeline status. The string is literally `'Pending ' + step` for every gated
 * step except Closing Confirmation, whose queue status is `Pending Export`.
 */
export const INVOICE_STATUS = {
  PendingMatching: 'Pending Matching',
  PendingImportControlDecision: 'Pending Import Control Decision',
  /** The alternative to Manager Approval for an invoice a Controller routing
      rule matches (owner, 2026-10-07 — D-303). */
  PendingControllerApprovalDecision: 'Pending Controller Approval Decision',
  PendingDepartmentApprovalDecision: 'Pending Department Approval Decision',
  PendingDirectorApprovalDecision: 'Pending Director Approval Decision',
  PendingOversightApprovalDecision: 'Pending Oversight Approval Decision',
  PendingExportControlDecision: 'Pending Export Control Decision',
  PendingExport: 'Pending Export',
  PendingDenialReviewDecision: 'Pending Denial Review Decision',
  // THE EIGHT ENDS (owner, 2026-10-14 — docs/decisions.md D-362), renamed
  // in place. The two completions read *Bulk Imported* and *Manually Entered
  // Into Point Click Care* until then. The six archives are the six answers to
  // the Archive pop-up's Category (`ARCHIVE_STATUS_FOR_CATEGORY`): one status
  // per answer, where there was one *Archived Not An Invoice* — now *Other* —
  // beside two that nothing wrote, *Archived Without Payment* and *Archived For
  // Fresh Invoice*, now *Payment Denied* and *Revised Invoice Requested*.
  ImportedIntoPointClickCare: 'Imported Into Point Click Care',
  TypedIntoPointClickCare: 'Typed Into Point Click Care',
  Other: 'Other',
  PaymentDenied: 'Payment Denied',
  RevisedInvoiceRequested: 'Revised Invoice Requested',
  Blank: 'Blank',
  DifferentTypeOfDocument: 'Different Type Of Document',
  Duplicate: 'Duplicate',
} as const
export type InvoiceStatus = (typeof INVOICE_STATUS)[keyof typeof INVOICE_STATUS]

/** All 17 live statuses, in pipeline order: nine on the way, then the eight ends. */
export const INVOICE_STATUSES: readonly InvoiceStatus[] = [
  INVOICE_STATUS.PendingMatching,
  INVOICE_STATUS.PendingImportControlDecision,
  INVOICE_STATUS.PendingControllerApprovalDecision,
  INVOICE_STATUS.PendingDepartmentApprovalDecision,
  INVOICE_STATUS.PendingDirectorApprovalDecision,
  INVOICE_STATUS.PendingOversightApprovalDecision,
  INVOICE_STATUS.PendingExportControlDecision,
  INVOICE_STATUS.PendingExport,
  INVOICE_STATUS.PendingDenialReviewDecision,
  INVOICE_STATUS.ImportedIntoPointClickCare,
  INVOICE_STATUS.TypedIntoPointClickCare,
  INVOICE_STATUS.Blank,
  INVOICE_STATUS.DifferentTypeOfDocument,
  INVOICE_STATUS.Duplicate,
  INVOICE_STATUS.PaymentDenied,
  INVOICE_STATUS.RevisedInvoiceRequested,
  INVOICE_STATUS.Other,
]

/** The eight live decision steps. Title Case, verbatim. */
export const DECISION_STEP = {
  ImportControl: 'Import Control Decision',
  /** Owner, 2026-10-07 (D-303): an alternative to Manager Approval, entered
      when a Controller routing rule matches the invoice at release. */
  ControllerApproval: 'Controller Approval Decision',
  DepartmentApproval: 'Department Approval Decision',
  DirectorApproval: 'Director Approval Decision',
  OversightApproval: 'Oversight Approval Decision',
  ExportControl: 'Export Control Decision',
  DenialReview: 'Denial Review Decision',
  ClosingConfirmation: 'Closing Confirmation Decision',
} as const
export type DecisionStep = (typeof DECISION_STEP)[keyof typeof DECISION_STEP]

export const DECISION_STEPS: readonly DecisionStep[] = [
  DECISION_STEP.ImportControl,
  DECISION_STEP.ControllerApproval,
  DECISION_STEP.DepartmentApproval,
  DECISION_STEP.DirectorApproval,
  DECISION_STEP.OversightApproval,
  DECISION_STEP.ExportControl,
  DECISION_STEP.DenialReview,
  DECISION_STEP.ClosingConfirmation,
]

/**
 * Steps that nothing decides at any more, kept only because stored rows carry
 * them (see the header). `AP/AR Approval Decision` was the second step until it
 * was merged into Import Control (owner, 2026-09-29 — D-215); `Manual Entry
 * Decision` was the hand-keying step until it was dropped (owner, 2026-10-07 —
 * D-311). Decision and correction rows written before those dates still name
 * them, and Decision History must show them.
 *
 * Deliberately NOT in `DECISION_STEP`: nothing may submit it, authorize against
 * it or route to it, and a `Record<DecisionStep, …>` table must not need a row
 * for it. Readers of history widen to `RecordedDecisionStep` instead.
 */
export const RETIRED_DECISION_STEP = {
  ApArApproval: 'AP/AR Approval Decision',
  ManualEntry: 'Manual Entry Decision',
} as const
type RetiredDecisionStep = (typeof RETIRED_DECISION_STEP)[keyof typeof RETIRED_DECISION_STEP]

/** Any step a STORED decision or correction row may carry: a live one or a retired one. */
export type RecordedDecisionStep = DecisionStep | RetiredDecisionStep

/** The label a person reads for a retired step. The live steps' labels are the app's. */
export const RETIRED_DECISION_STEP_LABELS: Readonly<Record<RetiredDecisionStep, string>> = {
  'AP/AR Approval Decision': 'AP/AR Approval',
  'Manual Entry Decision': 'Manual Entry',
}

/**
 * Decision outcomes. lower case — the split against DecisionStep is deliberate.
 * `escalated` was written only by the escalation worker, the three-day skip of
 * Manager Approval, until the worker was removed (owner, 2026-10-09 — D-336:
 * every step is required). The value stays for the rows it wrote, and it is
 * not submittable through `POST /decisions`. `SUBMITTABLE_DECISION_OUTCOMES`
 * below is what the route's parse layer checks against, so a person can never
 * submit it. Appended last: the object's order mirrors the SQL type, and
 * `ALTER TYPE … ADD VALUE` appends.
 */
export const DECISION_OUTCOME = {
  approved: 'approved',
  denied: 'denied',
  archivedWithoutPayment: 'archived without payment',
  archivedForFreshInvoice: 'archived for fresh invoice',
  /**
   * THE ARCHIVE, at Import Control and Denial Review. The Category picked in
   * its pop-up decides which of the six archive statuses the invoice reaches
   * (`ARCHIVE_STATUS_FOR_CATEGORY`, D-362). Stored as *archived not an invoice*
   * until 2026-10-14, when five of its six answers stopped being about
   * something that is not an invoice.
   */
  archived: 'archived',
  /**
   * EXPORT CONTROL ONLY. The controller has keyed the invoice into Point Click
   * Care by hand because the CSV import does not fit it, and records that here:
   * the button is **Typed Into Point Click Care**, and the invoice
   * ends at the terminal status of the same name, tagged
   * `manual_entry_requested` (owner, 2026-10-07 — `docs/decisions.md` D-311).
   * From 2026-08-31 until then it diverted the invoice to a Manual Entry step,
   * where someone else keyed it in. The stored value keeps its old wording.
   */
  requiresManualEntry: 'requires manual entry',
  escalated: 'escalated',
  /**
   * DENIAL REVIEW ONLY (owner, 2026-09-25). The reviewer sends a denied invoice
   * back to the first qualifying approval step, routed as the release routes
   * (owner, 2026-10-08 — D-324). From D-312 (2026-10-07) until then it went to
   * Import Control, flagged, and before that to the step that denied it.
   * Appended last, like every value added after the type was first created.
   */
  reinstated: 'reinstated',
} as const
export type DecisionOutcome = (typeof DECISION_OUTCOME)[keyof typeof DECISION_OUTCOME]

export const DECISION_OUTCOMES: readonly DecisionOutcome[] = [
  DECISION_OUTCOME.approved,
  DECISION_OUTCOME.denied,
  DECISION_OUTCOME.archivedWithoutPayment,
  DECISION_OUTCOME.archivedForFreshInvoice,
  DECISION_OUTCOME.archived,
  DECISION_OUTCOME.requiresManualEntry,
  DECISION_OUTCOME.escalated,
  DECISION_OUTCOME.reinstated,
]

/**
 * Every outcome a person may submit through `POST /decisions` — everything
 * except `escalated`, which only the escalation worker wrote (removed by
 * D-336). This is the
 * enum the route's parse layer (`backend/src/http/routes/decisions.ts`) checks
 * against, so a submitted `escalated` is refused at 400 `validation_failed`
 * before any authority gate runs.
 */
export const SUBMITTABLE_DECISION_OUTCOMES: readonly DecisionOutcome[] = DECISION_OUTCOMES.filter(
  (outcome) => outcome !== DECISION_OUTCOME.escalated,
)

/**
 * The reason category on a decision. The STORED value is the key, not the label.
 *
 * REQUIRED, NOT OPTIONAL, ON THE THREE OUTCOMES A PERSON CHOOSES FROM A DIALOG
 * (owner, 2026-09-15: "The Category picker for denials should not be optional
 * ... Add the same mandatory Category pickers to the pop-ups for Not An Invoice
 * ... and Move To Manual Entry (Split Between Companies, Other). In all three
 * cases, make the Reason required only if Other is chosen as the category."). It was optional everywhere until that date, with a
 * required free-text reason beside it — so the thing that could be counted was
 * the thing nobody had to fill in, and the thing everybody had to fill in could
 * not be counted. That is now the other way round.
 *
 * ONE ENUM, THREE VOCABULARIES. `reason_category` is one column, and the
 * outcome decides which values are legal in it —
 * `REASON_CATEGORIES_FOR_OUTCOME` below, enforced server-side in
 * `validateReason`, not only in the dialogs.
 */
export const REASON_CATEGORY = {
  // Deny.
  price_discrepancy: 'price_discrepancy',
  wrong_company: 'wrong_company',
  duplicate: 'duplicate',
  unauthorized_purchase: 'unauthorized_purchase',
  // The Archive dialog (owner, 2026-09-15: "Offer Category options of Blank,
  // Duplicate, Not An Invoice, and Other"). `duplicate` is shared with the
  // denial list above, which is why it is not repeated here. The button that
  // opens that dialog was itself called *Not An Invoice* until the same day and
  // is now **Archive**, so these words name one of its answers rather than the
  // action.
  blank: 'blank',
  // *Not An Invoice* until 2026-10-14 (D-362), stored `not_an_invoice`.
  different_type_of_document: 'different_type_of_document',
  // The Archive pop-up's two new answers (owner, 2026-10-14 — D-362).
  payment_denied: 'payment_denied',
  revised_invoice_requested: 'revised_invoice_requested',
  // Move To Manual Entry (owner, 2026-09-15). No dialog offers it since
  // 2026-10-07 (D-315); it stays because decisions written before then carry it.
  split_between_companies: 'split_between_companies',
  // Typed Into Point Click Care (owner, 2026-10-07 — D-315).
  pcc_import_error: 'pcc_import_error',
  // Hold (owner, 2026-09-25). Stored on `holds.reason_category`, not on a
  // decision — a hold is not a decision — but it is the same kind of answer to
  // the same kind of dialog, so it shares the one enum.
  awaiting_vendor_response: 'awaiting_vendor_response',
  awaiting_internal_information: 'awaiting_internal_information',
  disputed_charge: 'disputed_charge',
  // All of them.
  other: 'other',
} as const
export type ReasonCategory = (typeof REASON_CATEGORY)[keyof typeof REASON_CATEGORY]

export const REASON_CATEGORIES: readonly ReasonCategory[] = [
  REASON_CATEGORY.price_discrepancy,
  REASON_CATEGORY.wrong_company,
  REASON_CATEGORY.duplicate,
  REASON_CATEGORY.unauthorized_purchase,
  REASON_CATEGORY.blank,
  REASON_CATEGORY.different_type_of_document,
  REASON_CATEGORY.payment_denied,
  REASON_CATEGORY.revised_invoice_requested,
  REASON_CATEGORY.split_between_companies,
  REASON_CATEGORY.pcc_import_error,
  REASON_CATEGORY.awaiting_vendor_response,
  REASON_CATEGORY.awaiting_internal_information,
  REASON_CATEGORY.disputed_charge,
  REASON_CATEGORY.other,
]

/** Stored key -> display label. TITLE CASE on every word (owner, 2026-09-15:
    the options "should use 'proper' capitalization") — these are the names of
    fixed categories, not sentence fragments, and they are read in a dropdown
    beside Title Case labels everywhere else in the app. */
export const REASON_CATEGORY_LABELS: Readonly<Record<ReasonCategory, string>> = {
  price_discrepancy: 'Price Discrepancy',
  wrong_company: 'Wrong Company',
  duplicate: 'Duplicate',
  unauthorized_purchase: 'Unauthorized Purchase',
  blank: 'Blank',
  different_type_of_document: 'Different Type Of Document',
  payment_denied: 'Payment Denied',
  revised_invoice_requested: 'Revised Invoice Requested',
  split_between_companies: 'Split Between Companies',
  pcc_import_error: 'PCC Import Error',
  awaiting_vendor_response: 'Awaiting Vendor Response',
  awaiting_internal_information: 'Awaiting Internal Information',
  disputed_charge: 'Disputed Charge',
  other: 'Other',
}

/**
 * `Failed` is an extraction error. The database enum also keeps `Rejected`, the
 * acceptability gate's old refusal: nothing has written it since uploads stopped
 * being refused (owner, 2026-08-10), no row carries it, and the code dropped it
 * (code review A-06, 2026-10-03). Removing an enum value from Postgres waits for
 * a rebuild (`docs/questions.md` §4.40).
 */
export const UPLOAD_STATUS = {
  Uploaded: 'Uploaded',
  Parsed: 'Parsed',
  Failed: 'Failed',
} as const
export type UploadStatus = (typeof UPLOAD_STATUS)[keyof typeof UPLOAD_STATUS]

/**
 * Where an in-flight upload has got to, in pipeline order (owner, 2026-09-03).
 *
 * `upload_status` says whether the extract worker has finished; it cannot say
 * what the pipeline is doing, and it goes `Parsed` BEFORE matching runs. So an
 * uploader watching the Upload toast saw one vague "Processing…" covering the
 * split, the extraction and the wait, then "Done" while flags were still being
 * raised. These four are written by the workers as they cross each boundary:
 *
 *  - `Splitting`  — the pass-0 page-splitting call is in flight (`extract`).
 *  - `Extracting` — pass 1 is in flight (`extract`).
 *  - `Matching`   — invoices are written and pass 2 is working through them.
 *  - `Finishing`  — the LAST invoice of the upload is being matched.
 *
 * **A null phase is not a fifth value.** Before the worker picks the message up
 * it means "queued"; once `status` is terminal it means "done". The pair
 * (`status`, `processing_phase`) is the whole answer, and the app reads it that
 * way — nothing infers a phase from elapsed time, which is what the old
 * client-side `processing` stage did.
 */
export const UPLOAD_PHASE = {
  Splitting: 'Splitting',
  Extracting: 'Extracting',
  Matching: 'Matching',
  Finishing: 'Finishing',
} as const
export type UploadPhase = (typeof UPLOAD_PHASE)[keyof typeof UPLOAD_PHASE]

/**
 * The `export_status` values. No code names one by value, so only the type is
 * kept. `Failed` was never written and is gone from here (owner, 2026-10-17 —
 * code review E-08); the Postgres enum keeps it until a rebuild
 * (`docs/questions.md` §4.40).
 */
export type ExportStatus = 'Received' | 'Completed'

/**
 * WHICH SOURCE WON THE LINE, in precedence order. `Manual` is a human edit.
 *
 * THERE IS ONE LADDER, AND A SOURCE WINS WHOLE (owner, 2026-09-10;
 * `docs/plans/2026-09-10-general-coding-rules.md` §1a). The winner supplies the
 * account AND the department it carried; halves are never taken from two
 * different sources. `DEPARTMENT_SOURCE` below records the same winner from the
 * department's side, which is why the two enums share most of their values.
 *
 * The order:
 *
 *   vendor rule -> pass 2's pick among contested vendor rules
 *     -> general rule -> pass 2's pick among contested general rules
 *     -> a code printed on the LINE -> a code printed on the whole INVOICE
 *     -> precedent -> pass 2's own inference
 *
 * `DocumentHandwritten` is gone with the handwritten column: no incoming invoice
 * carries a handwritten GL code under a scan-from-envelope pipeline, so the rung
 * only ever invited the model to over-read some other number.
 */
export const ACCOUNT_SOURCE = {
  Precedent: 'Precedent',
  VendorRule: 'VendorRule',
  /**
   * Two or more of the MATCHED VENDOR's coding rules disagreed (a "contested
   * set") and pass 2 picked one — item 4. Reachable only when the contested set
   * has at least one rule with a non-blank `description_scope`. Ranks ABOVE
   * `Precedent` since 2026-09-10 (G5): the owner's hierarchy reaches precedent
   * only when no rule matched at all. The reasoning it replaced — that approved
   * history beats a model's choice between rules that were never decisions on
   * their own — is superseded, not overlooked.
   */
  VendorRuleArbitrated: 'VendorRuleArbitrated',
  /**
   * A coding rule that names NO vendor — it matched on the line's description
   * alone (G1). Ranks below every rule belonging to the matched vendor and
   * above every printed code.
   */
  GeneralRule: 'GeneralRule',
  /** The same, arbitrated: two general rules disagreed and pass 2 picked one (G6). */
  GeneralRuleArbitrated: 'GeneralRuleArbitrated',
  AiInferred: 'AiInferred',
  DocumentPrinted: 'DocumentPrinted',
  Manual: 'Manual',
} as const
export type AccountSource = (typeof ACCOUNT_SOURCE)[keyof typeof ACCOUNT_SOURCE]

/** Stored value -> in-app label. */
export const ACCOUNT_SOURCE_LABELS: Readonly<Record<AccountSource, string>> = {
  Precedent: 'Reused From a Prior Invoice',
  VendorRule: 'Vendor Coding Rule',
  VendorRuleArbitrated: 'Vendor Coding Rule (AI Picked Between Conflicting Rules)',
  GeneralRule: 'General Coding Rule',
  GeneralRuleArbitrated: 'General Coding Rule (AI Picked Between Conflicting Rules)',
  AiInferred: 'AI Suggestion',
  DocumentPrinted: 'From Document (Printed)',
  Manual: 'Set Manually',
}

/**
 * Where a printed GL code was attached on the page (G11, owner 2026-09-10).
 *
 * Pass 1 already decides this — "a code aligned to a specific line item applies to
 * that line only; a code appearing once for the whole document applies to every
 * line" — but until now nothing recorded the answer, so both the ladder and
 * `GLCodeLineMismatch` had to infer it from "every coded line carries the same
 * code". That misreads a real controller coding sheet whose rows happen to share
 * a code.
 *
 * NULL beside no code, and beside a code pass 1 gave no scope for (code review
 * B-08, D-267): no printed rung takes such a code, the flag does not treat it as
 * a stamp, and the line's confidence is held under the flag threshold so a
 * person looks.
 */
export const PRINTED_CODE_SCOPE = {
  line: 'line',
  document: 'document',
} as const
export type PrintedCodeScope = (typeof PRINTED_CODE_SCOPE)[keyof typeof PRINTED_CODE_SCOPE]

/**
 * WHERE THE DEPARTMENT CAME FROM. NOT a ladder of its own — the department is
 * READ OFF THE WINNER of the one ladder above (owner, 2026-09-10):
 *
 *   department = winner's own department, where it carried one
 *              ?? the chart's only legal department for the settled account
 *              ?? null, for a human to set at approval
 *
 * So these values name the SAME source `ACCOUNT_SOURCE` named, with two
 * differences. `ChartImplied` is the second line above — the 76 of 88 active
 * accounts that admit exactly one department; the other 12 reach a human
 * where the winner carried nothing. And there is no `AiInferred`: THE MODEL IS
 * NEVER A RUNG HERE, which is the point of the whole account / department
 * split — a line description reveals what was bought and never who it was for, so
 * a model asked for a department is guessing, and a guess presented as an answer
 * is what a reviewer rubber-stamps.
 *
 * `Precedent` IS RETIRED (G12, owner 2026-09-10), superseding his own item 6 of
 * 2026-08-31 which added it: "precedent does not make sense for department.
 * Gloves could be for nursing one day and dietary the next day." Nothing writes
 * it, and since code review B-08 (2026-10-03) nothing here names it: the value
 * stays only in the Postgres enum (`schema.sql`), which cannot drop one without
 * a table rewrite.
 */
export const DEPARTMENT_SOURCE = {
  ChartImplied: 'ChartImplied',
  VendorRule: 'VendorRule',
  /** The winning rule named no vendor (G1/G7). */
  GeneralRule: 'GeneralRule',
  DocumentPrinted: 'DocumentPrinted',
  Manual: 'Manual',
} as const
export type DepartmentSource = (typeof DEPARTMENT_SOURCE)[keyof typeof DEPARTMENT_SOURCE]

/** Stored value -> in-app label. */
export const DEPARTMENT_SOURCE_LABELS: Readonly<Record<DepartmentSource, string>> = {
  ChartImplied: 'Implied by the Chart',
  VendorRule: 'Vendor Coding Rule',
  GeneralRule: 'General Coding Rule',
  DocumentPrinted: 'From Document (Printed)',
  Manual: 'Set Manually',
}

/**
 * The two kinds of Additional Detail a line item can owe (owner item 11,
 * 2026-08-19; triggers restated by D-190, 2026-09-28). A line may owe both.
 *
 *  - `marketing` — the line's account carries `accounts.detail_kind =
 *    'marketing'` (`67-518` today). Data, never a hard-coded account id.
 *  - `transportation` — the line was copied in from Fleet Maintenance
 *    (`source_system = 'fleet_maintenance'`), which sends the detail filled
 *    in: its category, action, parts and labor. Read-only here; nobody in this
 *    app picks or edits it (owner, 2026-10-19 — D-381). No account carries
 *    this value.
 */
export const LINE_ITEM_DETAIL_KIND = {
  marketing: 'marketing',
  transportation: 'transportation',
} as const
export type LineItemDetailKind = (typeof LINE_ITEM_DETAIL_KIND)[keyof typeof LINE_ITEM_DETAIL_KIND]

/**
 * How the invoice's company was decided. The matching worker writes
 * `UploaderSelected`, `UploaderDomain`, `CompanyAlias` and `AiMatched`; the
 * Import Control pick writes `Manual`.
 *
 * `ScannerDefault` is RETIRED (owner, 2026-10-03 — D-280): the scanner rung
 * went with the Scanners feature. The Postgres enum keeps the value.
 *
 * `LineCompanies` is RETIRED (code review B-05, 2026-10-03): it named a
 * company the line items set, and lines carry no company now. The Postgres
 * enum keeps the value, which cannot be dropped; nothing writes it.
 *
 * The four worker values are the four rungs of `resolveCompany`, in
 * precedence order — see that function's header comment for why the order is
 * what it is:
 *
 *  * `UploaderSelected` — the person filing the upload picked the company on
 *    the Upload form (owner item 21, 2026-08-31). Validated server-side
 *    against that caller's own company scope, so it can only name a company
 *    they already hold authority over.
 *  * `UploaderDomain` — `company_email_domains` on the sender's mail domain.
 *  * `CompanyAlias` — the printed bill-to name is an authored alias for one
 *    company (owner item 18, 2026-08-31, `company_aliases`).
 *  * `AiMatched` — pass 2's answer, re-validated against the active master.
 *
 * `SourceSystem` is not a rung of `resolveCompany`: an invoice copied in from
 * another system (`SOURCE_SYSTEM` below) arrives with its company already
 * decided, because that system belongs to one company — Fleet Maintenance is
 * `PACE Healthcare Transportation`'s (owner, 2026-10-19 — D-380).
 */
export const COMPANY_SOURCE = {
  UploaderSelected: 'UploaderSelected',
  UploaderDomain: 'UploaderDomain',
  CompanyAlias: 'CompanyAlias',
  AiMatched: 'AiMatched',
  Manual: 'Manual',
  SourceSystem: 'SourceSystem',
} as const
export type CompanySource = (typeof COMPANY_SOURCE)[keyof typeof COMPANY_SOURCE]

/** `inferred` raises CompanyInferredFromLetterhead. */
export const COMPANY_NAME_SOURCE = {
  labeled: 'labeled',
  inferred: 'inferred',
} as const
export type CompanyNameSource = (typeof COMPANY_NAME_SOURCE)[keyof typeof COMPANY_NAME_SOURCE]

/**
 * lower_snake (ratified ruling 8). Anything other than `invoice` raises the
 * NonInvoice flag. `unclassified` is HOST-RESERVED: it is written only on the
 * record for a document whose own extraction call failed, and the extraction
 * prompt forbids the model from emitting it. `statement` and `cover_sheet` are
 * no longer emitted (D-338) and stay only because older rows carry them.
 */
export const DOCUMENT_TYPE = {
  invoice: 'invoice',
  statement: 'statement',
  cover_sheet: 'cover_sheet',
  other: 'other',
  unclassified: 'unclassified',
} as const
export type DocumentType = (typeof DOCUMENT_TYPE)[keyof typeof DOCUMENT_TYPE]

/**
 * The document types the extraction model may return (D-338): whether the
 * document is an invoice, and nothing finer. Excludes `unclassified`.
 */
export const MODEL_DOCUMENT_TYPES: readonly DocumentType[] = [
  DOCUMENT_TYPE.invoice,
  DOCUMENT_TYPE.other,
]

/** Capitalized — unlike document_type. Participant resolution was never built. */
export const ENTITY_TYPE = {
  Vehicle: 'Vehicle',
  Staff: 'Staff',
  Participant: 'Participant',
} as const
export type EntityType = (typeof ENTITY_TYPE)[keyof typeof ENTITY_TYPE]

export const MATCH_SOURCE = {
  VehicleList: 'VehicleList',
  StaffDirectory: 'StaffDirectory',
  Census: 'Census',
  AiInferred: 'AiInferred',
  Manual: 'Manual',
  Unmatched: 'Unmatched',
} as const
export type MatchSource = (typeof MATCH_SOURCE)[keyof typeof MATCH_SOURCE]

/**
 * The four approval tiers are oversight / director / department-head /
 * controller. Director and department-head are the default path; oversight
 * and controller are required only by a routing rule (D-120, D-303, D-336).
 * None is ever skipped for want of a seat.
 *
 * `analytics-dashboard` and `analytics-reporting` (owner, 2026-10-01 —
 * `docs/decisions.md` D-258) are the Analytics tab's two seats, ONE COMPANY
 * EACH: "Dashboard / SeniorLIFE Johnstown" shows that company's invoices on the
 * Dashboard and nowhere else. Neither is an approval tier and
 * neither confers record access on its own — a seat counts ONLY under its own
 * tab's view-only lens (`analyticsLensFor` in `backend/src/domain/authority.ts`).
 * They replaced the org-wide Analytics user roles.
 */
export const ASSIGNMENT_ROLE = {
  oversight: 'oversight',
  director: 'director',
  departmentHead: 'department-head',
  /** The Controller Approval seat (owner, 2026-10-07 — D-303), one company
      each, like `director` and `department-head`. Appended to the Postgres
      enum with `ALTER TYPE … ADD VALUE`. */
  controller: 'controller',
  analyticsDashboard: 'analytics-dashboard',
  analyticsReporting: 'analytics-reporting',
} as const
export type AssignmentRole = (typeof ASSIGNMENT_ROLE)[keyof typeof ASSIGNMENT_ROLE]

export const ASSIGNMENT_ROLES: readonly AssignmentRole[] = Object.values(ASSIGNMENT_ROLE)

/** The two Analytics seats, which count only under their own tab's lens. */
export const ANALYTICS_ASSIGNMENT_ROLES: readonly AssignmentRole[] = [
  ASSIGNMENT_ROLE.analyticsDashboard,
  ASSIGNMENT_ROLE.analyticsReporting,
]

export function isAnalyticsAssignmentRole(role: AssignmentRole): boolean {
  return ANALYTICS_ASSIGNMENT_ROLES.includes(role)
}

/** True for a string the code knows as an assignment role. A stored row whose
    role fails this is invisible and grants nothing (D-293). */
export function isAssignmentRole(value: string): value is AssignmentRole {
  return (ASSIGNMENT_ROLES as readonly string[]).includes(value)
}

/** `'department-head'` displays as **Manager** (owner item 8, 2026-08-19,
    `docs/decisions.md` D-43 — "Department Approval" → "Manager Approval"
    everywhere a user reads it): the stored token is unchanged. The app's
    `ASSIGNMENT_ROLE_LABEL` (`app/src/lib/constants.ts`) is built from it. */
export const ASSIGNMENT_ROLE_LABELS: Readonly<Record<AssignmentRole, string>> = {
  // THE STEP'S OWN NAME, in full (owner, 2026-09-14). The bare words read as
  // job titles; these three are approval steps and `TIMELINE_NODES` has always
  // called them so. The STORED tokens are untouched.
  oversight: 'Oversight Approval',
  director: 'Director Approval',
  'department-head': 'Manager Approval',
  controller: 'Controller Approval',
  // The tab names, as on the Analytics screen (D-258).
  'analytics-dashboard': 'Dashboard',
  'analytics-reporting': 'Reporting',
}

export const ASSIGNMENT_ACTION = {
  appoint: 'appoint',
  retire: 'retire',
} as const
export type AssignmentAction = (typeof ASSIGNMENT_ACTION)[keyof typeof ASSIGNMENT_ACTION]

/**
 * WHAT AN APPOINTMENT IS ABOUT: one company, or the organization (owner,
 * 2026-09-22 — `docs/decisions.md` D-134: *"'All' is not a company but an
 * 'apply to all' kind of thing."*).
 *
 * `company` names a company in `company_id`; `organization` names none, and
 * `company_id` is null. Only `oversight` is ever `organization`, and every
 * `oversight` row is — `assignments_oversight_is_org_wide` in
 * `backend/db/schema.sql` refuses anything else, and `canAppoint`
 * (`backend/src/domain/authority.ts`) refuses it first so the caller gets a 403
 * rather than a constraint violation as a 500.
 *
 * It exists instead of a `companies` row called `All`, which is how the org-wide
 * seat was stored for the seven days between D-124 and D-134, and instead of a
 * bare null — because the fact is then STORED rather than DERIVED from `role`.
 * A null says only "no company", and reading it as "org-wide" means knowing that
 * oversight-means-org-wide; this field says which it is, so `assignmentIsOrgWide`
 * reads one column and a second org-wide role later costs no reader a change.
 * It is NOT what refuses a `director` row with no company — the constraints
 * named above do that, and would do it under a bare null just as flatly.
 */
export const ASSIGNMENT_COMPANY_SCOPE = {
  company: 'company',
  organization: 'organization',
} as const
export type AssignmentCompanyScope =
  (typeof ASSIGNMENT_COMPANY_SCOPE)[keyof typeof ASSIGNMENT_COMPANY_SCOPE]

/**
 * The scope an appointment in this role must carry. `oversight` is the org-wide
 * seat and nothing else is — the one place that rule is written on this side of
 * the wire, so a route, a screen and a test cannot each decide it separately.
 */
export function companyScopeForRole(role: AssignmentRole): AssignmentCompanyScope {
  return role === ASSIGNMENT_ROLE.oversight
    ? ASSIGNMENT_COMPANY_SCOPE.organization
    : ASSIGNMENT_COMPANY_SCOPE.company
}

/**
 * Capitalized, unlike the decision outcomes. ONE VALUE IN CODE (code review
 * C-13, 2026-10-03): every assignment row is written `Applied`. The Postgres
 * enum still carries `Pending` and `Rejected` from the Dataverse broker, which
 * wrote a pending row for a flow to flip; nothing here writes either, and an
 * enum value cannot be dropped without a rebuild (`docs/questions.md` §4.40).
 */
export const ASSIGNMENT_REQUEST_STATUS = {
  Applied: 'Applied',
} as const
export type AssignmentRequestStatus =
  (typeof ASSIGNMENT_REQUEST_STATUS)[keyof typeof ASSIGNMENT_REQUEST_STATUS]

export const SUBSCRIPTION_STATUS = {
  active: 'active',
  paused: 'paused',
  superseded: 'superseded',
  cancelled: 'cancelled',
} as const
export type SubscriptionStatus = (typeof SUBSCRIPTION_STATUS)[keyof typeof SUBSCRIPTION_STATUS]

/**
 * Reminder step selections and the app's pipeline node keys. THE TOKENS ARE
 * HYPHENATED. The retired legacy tokens `primary` (-> director) and `secondary`
 * (-> oversight) are NOT carried; a back-fill rewrites them in the load script.
 * `ap-ar` is retired the same way (owner, 2026-09-29 — D-215): the AP/AR node is
 * gone with the step, and `post-schema.sql` rewrites a stored `ap-ar` selection
 * to `import-control`, where those invoices now wait.
 */
export const PIPELINE_STEP_KEY = {
  importControl: 'import-control',
  controller: 'controller',
  department: 'department',
  director: 'director',
  oversight: 'oversight',
  exportControl: 'export-control',
  closingConfirmation: 'closing-confirmation',
  denialReview: 'denial-review',
} as const
export type PipelineStepKey = (typeof PIPELINE_STEP_KEY)[keyof typeof PIPELINE_STEP_KEY]

/** The timeline nodes: every pipeline step key plus `archives`. */
export const NODE_KEY = {
  ...PIPELINE_STEP_KEY,
  archives: 'archives',
} as const
export type NodeKey = (typeof NODE_KEY)[keyof typeof NODE_KEY]

/** `ddd` abbreviations, as the Eastern-time due test compares them. */
const DAY_OF_WEEK = {
  Mon: 'Mon',
  Tue: 'Tue',
  Wed: 'Wed',
  Thu: 'Thu',
  Fri: 'Fri',
  Sat: 'Sat',
  Sun: 'Sun',
} as const
export type DayOfWeek = (typeof DAY_OF_WEEK)[keyof typeof DAY_OF_WEEK]

export const DAYS_OF_WEEK: readonly DayOfWeek[] = [
  DAY_OF_WEEK.Mon,
  DAY_OF_WEEK.Tue,
  DAY_OF_WEEK.Wed,
  DAY_OF_WEEK.Thu,
  DAY_OF_WEEK.Fri,
  DAY_OF_WEEK.Sat,
  DAY_OF_WEEK.Sun,
]

export const VENDOR_ALIAS_SOURCE = {
  Learned: 'Learned',
  Manual: 'Manual',
  Backfill: 'Backfill',
} as const
export type VendorAliasSource = (typeof VENDOR_ALIAS_SOURCE)[keyof typeof VENDOR_ALIAS_SOURCE]

/** Who authored a vendor coding rule. Mirrors the SQL `coding_rule_source`. */
export const CODING_RULE_SOURCE = {
  Controller: 'Controller',
  Learned: 'Learned',
} as const
export type CodingRuleSource =
  (typeof CODING_RULE_SOURCE)[keyof typeof CODING_RULE_SOURCE]

/**
 * The fleet director's own vocabulary, verbatim, and the ONLY input to a
 * vehicle's `is_active` (owner ruling 2026-08-28, `docs/decisions.md` D-65).
 * Nobody toggles a vehicle's listing by hand: `In Service` is Active, and
 * `Out of Service` and `Sold` are both Inactive.
 */
export const VEHICLE_STATUS = {
  InService: 'In Service',
  OutOfService: 'Out of Service',
  Sold: 'Sold',
} as const
export type VehicleStatus = (typeof VEHICLE_STATUS)[keyof typeof VEHICLE_STATUS]

export const VEHICLE_STATUSES: readonly VehicleStatus[] = [
  VEHICLE_STATUS.InService,
  VEHICLE_STATUS.OutOfService,
  VEHICLE_STATUS.Sold,
]

/**
 * `vehicles.is_active` from `vehicles.status`. THE ONE PLACE THE RULE LIVES —
 * every writer (the create route, the patch route, the fleet spreadsheet import,
 * the mock, the seed) calls this rather than re-spelling the comparison, and
 * `schema.sql`'s `vehicles_active_follows_status` CHECK refuses any row that
 * disagrees with it.
 *
 * Whitespace is trimmed first. Not for the editor — its Status control is a
 * closed dropdown over `VEHICLE_STATUSES` (D-66) — but for the fleet
 * spreadsheet, whose cells arrive with whatever padding the export put in them.
 *
 * **A blank or missing status counts as Active**, deliberately: a create with no
 * status defaults to `In Service` (the same `?? 'In Service'` the create route
 * and the import's add path already applied), so the derivation must agree with
 * that default or a brand-new vehicle would land retired. Anything else that is
 * not `In Service` — including a label the fleet director invents later — is
 * Inactive, which fails closed: an unrecognised label means "not in service".
 */
export function vehicleIsActiveFor(status: string | null | undefined): boolean {
  const label = (status ?? '').trim()
  return label === '' || label === VEHICLE_STATUS.InService
}

/** `auto` clears on Save once the condition holds; `explicit` needs a click. */
export const FLAG_RESOLUTION = {
  auto: 'auto',
  explicit: 'explicit',
} as const
export type FlagResolution = (typeof FLAG_RESOLUTION)[keyof typeof FLAG_RESOLUTION]

/**
 * Internal step / platform roles. Replaces the Dataverse internal step TEAMS.
 *
 * DELIBERATELY EXCLUDES ALL THREE APPROVAL TIERS, oversight included: oversight,
 * director and department-head authority is DATA in `assignments`, never a role
 * grant. A second source of truth for the same authority is exactly the drift
 * the port should not inherit. Document access therefore derives from "an internal
 * role OR an active assignment in any of the three approval tiers" (owner ruling
 * R2, 2026-08-10 — it was oversight alone before that).
 */
export const USER_ROLE = {
  import_control: 'import_control',
  export_control: 'export_control',
  closing_confirmation: 'closing_confirmation',
  denial_review: 'denial_review',
  // MIGRATION ADDITION (owner item 29, 2026-08-28): PER-TAB roles inside the
  // References and Rules blocks. One value per tab on each screen, so a
  // person can be given the Vehicles master without also being given the
  // vendor list. They are the ONLY grant for either screen (`authority.ts`
  // `canManageReferenceSection` / `canManageRulesSection`).
  //
  // NONE OF THESE TEN IS ON `isInternal`'s allow-list: a per-tab role grants
  // exactly its own tab's master data and confers no cross-company
  // row-level-security bypass. The masters carry no RLS of their own, so
  // nothing here needs one. See docs/security.md §2.1 and docs/decisions.md D-52.
  references_vendors: 'references_vendors',
  references_companies: 'references_companies',
  references_codes: 'references_codes',
  references_vehicles: 'references_vehicles',
  references_marketing: 'references_marketing',
  // `references_transportation` is DATABASE-ONLY since 2026-10-19 (D-381): the
  // Transportation tab went with the Transportation Categories, and Postgres
  // cannot drop an enum value.
  rules_vendors: 'rules_vendors',
  rules_companies: 'rules_companies',
  rules_codes: 'rules_codes',
  rules_guidance: 'rules_guidance',
  // The fifth Rules tab (owner, 2026-09-15): the criteria that forward an
  // invoice for Oversight Approval, and since 2026-10-07 (D-304) for
  // Controller Approval too — the tab reads "Routing". Same contract as its four neighbours --
  // additive, granting its own tab and nothing else, and NOT on `isInternal`'s
  // allow-list, so it confers no cross-company read and no document access.
  rules_oversight: 'rules_oversight',
  // A sixth Rules role, `rules_notifications`, opened the Upload Notifications
  // tab from D-306 until D-310 (both 2026-10-07, never deployed), when the list
  // moved to Assignments -> Notifications, an admin's tab.
  admin: 'admin',
} as const
export type UserRole = (typeof USER_ROLE)[keyof typeof USER_ROLE]

/** Every role the code knows, in `USER_ROLE`'s order — and every role `POST
    /user-roles` accepts. */
export const USER_ROLES: readonly UserRole[] = Object.values(USER_ROLE)

/** True for a string the code knows as a user role. A stored grant whose role
    fails this is invisible and grants nothing (D-293). */
export function isUserRole(value: string): value is UserRole {
  return (USER_ROLES as readonly string[]).includes(value)
}

/**
 * The References screen's tabs, as section keys — owner item 29, 2026-08-28.
 * The key IS the tab value in `app/src/screens/TrainingScreen.tsx`, so the
 * server's answer and the screen's tab list cannot drift.
 */
export const REFERENCE_SECTION = {
  vendors: 'vendors',
  companies: 'companies',
  codes: 'codes',
  vehicles: 'vehicles',
  marketing: 'marketing',
} as const
export type ReferenceSection = (typeof REFERENCE_SECTION)[keyof typeof REFERENCE_SECTION]

export const REFERENCE_SECTIONS: readonly ReferenceSection[] = [
  REFERENCE_SECTION.vendors,
  REFERENCE_SECTION.companies,
  REFERENCE_SECTION.codes,
  REFERENCE_SECTION.vehicles,
  REFERENCE_SECTION.marketing,
]

/** Section -> the `user_role` value that grants it on its own. */
export const REFERENCE_SECTION_ROLE: Readonly<Record<ReferenceSection, UserRole>> = {
  vendors: USER_ROLE.references_vendors,
  companies: USER_ROLE.references_companies,
  codes: USER_ROLE.references_codes,
  vehicles: USER_ROLE.references_vehicles,
  marketing: USER_ROLE.references_marketing,
}

/** The tab label each section carries. `codes` reads **Codes** on both
    screens (D-76, owner 2026-09-01 — *Accounts & Departments* between owner
    items 19 / 26, 2026-08-28, and that date; *Codes* before them). The key
    and the screens' own tab value are both `codes`, and both are state, not
    labels. Assignments names its per-tab roles from this map, so
    a tab and the role that grants it are renamed in one place. */
export const REFERENCE_SECTION_LABEL: Readonly<Record<ReferenceSection, string>> = {
  vendors: 'Vendors',
  companies: 'Companies',
  codes: 'Codes',
  vehicles: 'Vehicles',
  // Bare nouns since 2026-09-14 (owner: "let's shorten Marketing Categories to
  // just Marketing and Transportation Categories to just Transportation").
  // They read inside a segmented control beside Vehicles now, where "Categories"
  // on two of the three was the only word distinguishing nothing.
  marketing: 'Marketing',
}

/**
 * The name each References role goes by on the Assignments screen, where it
 * is appointed: the tab, then the table it opens (owner, 2026-09-29 — D-199).
 * The server's refusal for a missing role names it the same way (D-289), so
 * the reader can find the row to ask for.
 */
export const REFERENCE_SECTION_ROLE_LABEL: Readonly<Record<ReferenceSection, string>> = {
  vendors: 'Vendors',
  companies: 'Companies',
  codes: 'Codes',
  vehicles: 'Associated Entities - Vehicles',
  marketing: 'Additional Detail - Marketing Categories',
}

/** The Rules screen's tabs. Same contract as `REFERENCE_SECTION` above. */
export const RULES_SECTION = {
  vendors: 'vendors',
  companies: 'companies',
  codes: 'codes',
  guidance: 'guidance',
  // Trails Guidance rather than joining the first three, which are deliberately
  // ordered to match the References screen's own tabs (owner, 2026-08-14).
  oversight: 'oversight',
} as const
export type RulesSection = (typeof RULES_SECTION)[keyof typeof RULES_SECTION]

export const RULES_SECTIONS: readonly RulesSection[] = [
  RULES_SECTION.vendors,
  RULES_SECTION.companies,
  RULES_SECTION.codes,
  RULES_SECTION.guidance,
  RULES_SECTION.oversight,
]

export const RULES_SECTION_ROLE: Readonly<Record<RulesSection, UserRole>> = {
  vendors: USER_ROLE.rules_vendors,
  companies: USER_ROLE.rules_companies,
  codes: USER_ROLE.rules_codes,
  guidance: USER_ROLE.rules_guidance,
  oversight: USER_ROLE.rules_oversight,
}

export const RULES_SECTION_LABEL: Readonly<Record<RulesSection, string>> = {
  vendors: 'Vendors',
  companies: 'Companies',
  codes: 'Codes',
  guidance: 'Guidance',
  // "Routing" since 2026-10-07 (owner — D-304): the tab holds the Controller
  // Approval rules and the Oversight Approval rules, behind a switcher. The
  // key, the role token `rules_oversight` and the table stay as they were.
  oversight: 'Routing',
}

/**
 * Who an upload notification goes to, per company (owner, 2026-10-07 — D-306):
 * the company's AP/AR representative, two controllers, and anyone else. The
 * Postgres enum `upload_notification_kind` carries the same three values
 * (`userRoleVocabulary.test.ts` pairs them).
 */
const UPLOAD_NOTIFICATION_KIND = {
  ap_ar: 'ap_ar',
  controller: 'controller',
  other: 'other',
} as const
export type UploadNotificationKind = (typeof UPLOAD_NOTIFICATION_KIND)[keyof typeof UPLOAD_NOTIFICATION_KIND]

/**
 * WHAT A NOTIFICATION IS ABOUT (owner, 2026-10-07 — D-310: "Since the tab itself is
 * now just called Alerts, the table should work for all kinds of alerts.
 * Therefore, the leftmost column should be called Alert, and its value for all
 * rows at this point should be 'New Upload'."). Two values since D-336: the
 * upload notification, and Invoice Passed Without Any Approvals. The Postgres
 * enum `notification_type` carries the same values.
 */
export const NOTIFICATION_TYPE = {
  new_upload: 'new_upload',
  // An invoice whose routing rules required no approval step went straight to
  // Export Control (owner, 2026-10-09 — D-336: "Allow it, but let's add an
  // alert, called "Invoice Passed Without Any Approvals".").
  passed_without_approvals: 'passed_without_approvals',
  // The @mention email and the Custom Notification, kept in the Notifications
  // tab too (owner, 2026-10-19 — D-386: "We might as well be consistent.").
  // Nobody is listed for them on Assignments → Notifications.
  mention: 'mention',
  custom_notification: 'custom_notification',
} as const
export type NotificationType = (typeof NOTIFICATION_TYPE)[keyof typeof NOTIFICATION_TYPE]
/** The two types people are listed for on Assignments → Notifications (D-386). */
export type ListedNotificationType = typeof NOTIFICATION_TYPE.new_upload | typeof NOTIFICATION_TYPE.passed_without_approvals

/**
 * How a notification type reads on screen — the Notification column, the Notifications list and
 * the opened notification's title. `new_upload` reads "New Upload" (owner,
 * 2026-10-19). It read "New Invoices Upload" from 2026-10-07 (owner: "The New
 * Upload alert (please rename to New Invoices Upload)"); the stored value never changed.
 */
export const NOTIFICATION_TYPE_LABEL: Readonly<Record<NotificationType, string>> = {
  new_upload: 'New Upload',
  passed_without_approvals: 'Invoice Passed Without Any Approvals',
  mention: 'Comment Mention',
  custom_notification: 'Custom Notification',
}

/**
 * The notification types people are LISTED for, in the order Assignments →
 * Notifications shows them. A mention and a Custom Notification go to the
 * person mentioned and to the subscriber, so neither is here (D-386).
 */
export const NOTIFICATION_TYPES: readonly ListedNotificationType[] = [NOTIFICATION_TYPE.new_upload, NOTIFICATION_TYPE.passed_without_approvals]

/** Left to right, as the Assignments → Notifications table shows them. */
export const UPLOAD_NOTIFICATION_KINDS: readonly UploadNotificationKind[] = [
  UPLOAD_NOTIFICATION_KIND.ap_ar,
  UPLOAD_NOTIFICATION_KIND.controller,
  UPLOAD_NOTIFICATION_KIND.other,
]

/**
 * THE LISTS EACH NOTIFICATION KEEPS, per company. New Upload keeps three —
 * AP/AR, Controllers and Others — and its recipients each carry the `kind` of
 * their list. Invoice Passed Without Any Approvals keeps ONE list, so its
 * recipients carry no kind (`kind` is null; owner, 2026-10-09 — D-336: "There
 * is not a specific structure to who should be receiving this one."). An empty
 * entry here means exactly that: one list, no kind. The schema's CHECK
 * `upload_notification_recipients_kind_per_notification` and the create route enforce
 * the same pairing.
 */
export const NOTIFICATION_RECIPIENT_KINDS: Readonly<Record<ListedNotificationType, readonly UploadNotificationKind[]>> = {
  new_upload: UPLOAD_NOTIFICATION_KINDS,
  passed_without_approvals: [],
}

/** True when the notification keeps a single list, whose recipients carry no kind. */
export const isSingleListNotification = (notificationType: ListedNotificationType): boolean => NOTIFICATION_RECIPIENT_KINDS[notificationType].length === 0

/** The column headers (owner, 2026-10-07: shorter than his own suggestion, so they fit). */
export const UPLOAD_NOTIFICATION_KIND_LABEL: Readonly<Record<UploadNotificationKind, string>> = {
  ap_ar: 'AP/AR',
  controller: 'Controllers',
  other: 'Others',
}

/**
 * How many of each a company is expected to have: one AP/AR representative and
 * two controllers (owner, 2026-10-07: "a company representative (the AP/AR, to
 * be specific) plus two controllers are always receiving notifications").
 * A floor for the screen's warning, never a cap and never a refusal.
 */
export const UPLOAD_NOTIFICATION_MINIMUM: Readonly<Record<UploadNotificationKind, number>> = {
  ap_ar: 1,
  controller: 2,
  other: 0,
}

/**
 * The Analytics screen's tabs (owner, 2026-09-10). Same contract as
 * `REFERENCE_SECTION` and `RULES_SECTION` above: the key IS the tab value in
 * `app/src/screens/AnalyticsScreen.tsx`, so the server's answer and the screen's
 * tab list cannot drift.
 *
 * `dashboard` is the content the tab already carried; `reporting` is the new
 * criteria-and-PDF screen.
 */
export const ANALYTICS_SECTION = {
  dashboard: 'dashboard',
  reporting: 'reporting',
} as const
export type AnalyticsSection = (typeof ANALYTICS_SECTION)[keyof typeof ANALYTICS_SECTION]

export const ANALYTICS_SECTIONS: readonly AnalyticsSection[] = [
  ANALYTICS_SECTION.dashboard,
  ANALYTICS_SECTION.reporting,
]

/** Section -> the appointment that opens it, one company per seat (D-258). */
export const ANALYTICS_SECTION_ASSIGNMENT_ROLE: Readonly<Record<AnalyticsSection, AssignmentRole>> = {
  dashboard: ASSIGNMENT_ROLE.analyticsDashboard,
  reporting: ASSIGNMENT_ROLE.analyticsReporting,
}

/** The request header that asks for a tab's view-only lens on a SHARED read
    (an invoice opened from the Dashboard, a report's source pages). The tab's
    own routes (`/analytics/*`, `/reports*`) take their lens from the route and
    ignore it. Lower case: API Gateway lower-cases every header. */
export const ANALYTICS_LENS_HEADER = 'x-ap-analytics-lens'

export const ANALYTICS_SECTION_LABEL: Readonly<Record<AnalyticsSection, string>> = {
  dashboard: 'Dashboard',
  reporting: 'Reporting',
}

/**
 * The dimensions the dashboard's four-measure breakdown (§16.7) can group by.
 *
 * One endpoint per dimension would have been four near-identical files; one
 * endpoint with a checked parameter keeps the four measures — invoices, spend,
 * median days, denials — defined in exactly one place, which is what stops
 * "Invoices Per Vendor" and "Spend Per Vendor" counting different populations.
 */
export const ANALYTICS_DIMENSION = {
  step: 'step',
  vendor: 'vendor',
  company: 'company',
} as const
export type AnalyticsDimension = (typeof ANALYTICS_DIMENSION)[keyof typeof ANALYTICS_DIMENSION]

export const ANALYTICS_DIMENSIONS: readonly AnalyticsDimension[] = [
  ANALYTICS_DIMENSION.step,
  ANALYTICS_DIMENSION.vendor,
  ANALYTICS_DIMENSION.company,
]

/**
 * How §16.8 rolls up line-item spend. `account` groups by the trailing half,
 * `department` by the leading half, and `code` by the composed code the two
 * halves make. The app prints them as Account, Department and Code
 * (`docs/decisions.md` D-85).
 */
export const SPEND_GROUPING = {
  account: 'account',
  department: 'department',
  code: 'code',
} as const
export type SpendGrouping = (typeof SPEND_GROUPING)[keyof typeof SPEND_GROUPING]

export const SPEND_GROUPINGS: readonly SpendGrouping[] = [
  SPEND_GROUPING.account,
  SPEND_GROUPING.department,
  SPEND_GROUPING.code,
]

// ---------------------------------------------------------------------------
// OVERSIGHT RULES (owner, 2026-09-15)
//
// Oversight Approval stops being a present-if-staffed tier and becomes a set of
// criteria: an invoice matching any ACTIVE rule stops there, one matching none
// walks past. Design:
// docs/superpowers/specs/2026-09-15-oversight-approval-rules-design.md
//
// There is NO and/or control anywhere. Within a rule the conditions are ANDed,
// a multi-value entity condition is the internal `or`, and the RULE LIST is the
// outer `or`. A flat list mixing the two is ambiguous -- "vendor is X or total
// is over $1,000 and company is Y" has two readings -- and readers pick wrongly.
// ---------------------------------------------------------------------------

/**
 * THE FOUR APPROVAL STEPS A ROUTING RULE CAN REQUIRE (owner, 2026-10-09 —
 * D-336), in pipeline order. Since D-336 a routing rule is not a destination
 * but a set of required approvals: "In the Add/Edit Rule pop-up, list each of
 * the approval steps with toggles next to each one." The keys are the column
 * stems on `oversight_rules` and on `invoices`, where they hold the route the
 * release decided (D-350) (`requires_controller`,
 * `requires_manager`, `requires_director`, `requires_oversight`). `manager` is
 * Manager Approval, whose stored step is still `Department Approval Decision`
 * (D-43).
 */
const APPROVAL_REQUIREMENT = {
  controller: 'controller',
  manager: 'manager',
  director: 'director',
  oversight: 'oversight',
} as const
export type ApprovalRequirement = (typeof APPROVAL_REQUIREMENT)[keyof typeof APPROVAL_REQUIREMENT]

/** Pipeline order: Controller, Manager, Director, Oversight (D-336). */
export const APPROVAL_REQUIREMENTS: readonly ApprovalRequirement[] = [
  APPROVAL_REQUIREMENT.controller,
  APPROVAL_REQUIREMENT.manager,
  APPROVAL_REQUIREMENT.director,
  APPROVAL_REQUIREMENT.oversight,
]

/** Each requirement by its step's name, as the Routing tab's columns and
    toggles and History's routing rows say it. */
export const APPROVAL_REQUIREMENT_LABELS: Readonly<Record<ApprovalRequirement, string>> = {
  controller: 'Controller Approval',
  manager: 'Manager Approval',
  director: 'Director Approval',
  oversight: 'Oversight Approval',
}

/**
 * WHAT DECIDED AN INVOICE'S ROUTE at its release or reinstate (owner,
 * 2026-10-12 — D-350): an Override set on the invoice, the routing rules that
 * matched it, or, with neither, the default route. Stored on the invoice
 * (`invoices.route_source`) beside the four `requires_*` columns, and on the
 * decision that decided it (`decisions.route`).
 */
export const ROUTE_SOURCE = {
  override: 'override',
  rules: 'rules',
  default: 'default',
} as const
export type RouteSource = (typeof ROUTE_SOURCE)[keyof typeof ROUTE_SOURCE]

/**
 * WHERE A ROUTING RULE WAS CREATED (owner, 2026-10-12 — D-350, amended the same
 * day): on Rules → Routing (`manual`), or by an Override whose "Remember this
 * route" box was checked (`override`), which creates the vendor's own rule.
 * Set once, at creation, and never changed after (owner: "The source is the
 * source"): an edit on Rules → Routing, or an Override updating a rule a person
 * wrote, leaves it as it was. Rules → Routing filters on it, and the routing
 * logic ignores it: both kinds route alike.
 */
export const ROUTING_RULE_SOURCE = {
  manual: 'manual',
  override: 'override',
} as const
export type RoutingRuleSource = (typeof ROUTING_RULE_SOURCE)[keyof typeof ROUTING_RULE_SOURCE]

export const ROUTING_RULE_SOURCES: readonly RoutingRuleSource[] = [
  ROUTING_RULE_SOURCE.manual,
  ROUTING_RULE_SOURCE.override,
]

/**
 * THE ROUTING-RULE AUDIT LOG (owner, 2026-10-12 — D-350 amended: "I agree that
 * there should be an audit log"). One row per change to a rule, in
 * `routing_rule_changes`: where it was made — Rules → Routing
 * (`rules_routing`) or an Override at Import Control or Denial Review
 * (`override`) — and what it did. Server-side only; nothing on the wire reads
 * it.
 */
export type RoutingRuleChangePlace = 'rules_routing' | 'override'

export type RoutingRuleChangeAction = 'created' | 'updated' | 'activated' | 'deactivated'

/**
 * WHERE IN THE APP A DECISION WAS TAKEN (owner, 2026-10-08 — D-331: "When we
 * record an approval, can we record whether it happened via bulk selecting in
 * the main queue, single selecting in the main queue, or the button inside the
 * invoice modal?"). `bulk_queue` is the queue's selection bar (or an Export
 * Control company group), `queue_row` a row's own button, `modal` a button in
 * the invoice modal or its editor. Stored on `decisions.source` for every step
 * and outcome.
 *
 * CLIENT-REPORTED: the SPA says where the click was, and the server checks only
 * that the value is one of these. It is a usage signal, not an audit fact.
 * Null when the caller sent none, on every row written before 2026-10-08, and
 * on the escalation worker's skips.
 */
export const DECISION_SOURCE = {
  bulk_queue: 'bulk_queue',
  queue_row: 'queue_row',
  modal: 'modal',
} as const
export type DecisionSource = (typeof DECISION_SOURCE)[keyof typeof DECISION_SOURCE]

export const DECISION_SOURCES: readonly DecisionSource[] = [
  DECISION_SOURCE.bulk_queue,
  DECISION_SOURCE.queue_row,
  DECISION_SOURCE.modal,
]

/**
 * A condition's scope, which is also the FIRST CLAUSE of its own sentence; the
 * words a person reads are in `OVERSIGHT_SCOPE_CLAUSE`, not in these keys.
 *
 * `no_line` exists because negation applied to a quantifier is ambiguous:
 * "any line item's department is not Dietary" can mean "there is a non-Dietary
 * line" or "there are no Dietary lines", and people mean the second. Line-scoped
 * fields therefore carry POSITIVE OPERATORS ONLY and this scope does the
 * negating.
 */
export const OVERSIGHT_RULE_SCOPE = {
  invoice: 'invoice',
  any_line: 'any_line',
  no_line: 'no_line',
} as const
export type OversightRuleScope = (typeof OVERSIGHT_RULE_SCOPE)[keyof typeof OVERSIGHT_RULE_SCOPE]

export const OVERSIGHT_RULE_SCOPES: readonly OversightRuleScope[] = [
  OVERSIGHT_RULE_SCOPE.invoice,
  OVERSIGHT_RULE_SCOPE.any_line,
  OVERSIGHT_RULE_SCOPE.no_line,
]

/** Lower case, and a curly apostrophe: these are clauses inside a sentence, not
    the names of settings. The same rule the Companies tab's `allowed` /
    `not allowed` and the Codes tab's `any text` / `specific text` follow. */
export const OVERSIGHT_SCOPE_CLAUSE: Readonly<Record<OversightRuleScope, string>> = {
  invoice: 'the invoice’s',
  any_line: 'any line item’s',
  no_line: 'no line item’s',
}

/** True for the two scopes that quantify over line items. */
export function isLineScope(scope: OversightRuleScope): boolean {
  return scope === OVERSIGHT_RULE_SCOPE.any_line || scope === OVERSIGHT_RULE_SCOPE.no_line
}

/**
 * Every field a condition may name.
 *
 * The internal invoice id is DELIBERATELY absent: it is a UUID, so a standing
 * rule keyed on one would fire for exactly one invoice, once, and never again.
 */
export const OVERSIGHT_RULE_FIELD = {
  // Which system the invoice came from (owner, 2026-10-19 — D-382): lets a
  // rule route Fleet Maintenance's invoices on their own. Its choices are
  // `SOURCE_SYSTEMS`, not a master, so the editor lists them itself.
  source: 'source',
  vendor: 'vendor',
  company: 'company',
  external_invoice_id: 'external_invoice_id',
  uploaded_date: 'uploaded_date',
  invoice_date: 'invoice_date',
  subtotal: 'subtotal',
  tax: 'tax',
  shipping: 'shipping',
  prior_balance: 'prior_balance',
  total: 'total',
  line_description: 'line_description',
  line_account: 'line_account',
  line_department: 'line_department',
  line_code: 'line_code',
  line_price: 'line_price',
  line_quantity: 'line_quantity',
  line_total: 'line_total',
} as const
export type OversightRuleField = (typeof OVERSIGHT_RULE_FIELD)[keyof typeof OVERSIGHT_RULE_FIELD]

export const OVERSIGHT_RULE_FIELDS: readonly OversightRuleField[] =
  Object.values(OVERSIGHT_RULE_FIELD)

/**
 * The label a person reads. TITLE CASE (owner, 2026-09-21): these are the names
 * of fields on an invoice, and the app treats a field name as a proper noun
 * wherever it prints one. The scope and operator clauses around them stay lower
 * case, because those are ordinary words in the sentence rather than names.
 *
 * Account is the trailing half, Department the leading half and Code the
 * whole NNNNNN-NN-NNN -- the app's own vocabulary (`docs/decisions.md` D-85),
 * matching Rules -> Codes and References -> Codes. These are display words;
 * the wire carries the field names (`line_account`, `line_department`,
 * `line_code`).
 */
export const OVERSIGHT_FIELD_LABEL: Readonly<Record<OversightRuleField, string>> = {
  source: 'Source',
  vendor: 'Vendor',
  company: 'Company',
  external_invoice_id: 'External Invoice ID',
  uploaded_date: 'Uploaded Date',
  invoice_date: 'Invoice Date',
  subtotal: 'Subtotal',
  tax: 'Tax',
  shipping: 'Shipping',
  prior_balance: 'Prior Balance',
  total: 'Total',
  line_description: 'Description',
  line_account: 'Account',
  line_department: 'Department',
  line_code: 'Code',
  line_price: 'Price',
  line_quantity: 'Quantity',
  line_total: 'Total',
}

/** Which grain each field reads from. A field is offered only under its own. */
export const OVERSIGHT_FIELD_SCOPE: Readonly<Record<OversightRuleField, 'invoice' | 'line'>> = {
  source: 'invoice',
  vendor: 'invoice',
  company: 'invoice',
  external_invoice_id: 'invoice',
  uploaded_date: 'invoice',
  invoice_date: 'invoice',
  subtotal: 'invoice',
  tax: 'invoice',
  shipping: 'invoice',
  prior_balance: 'invoice',
  total: 'invoice',
  line_description: 'line',
  line_account: 'line',
  line_department: 'line',
  line_code: 'line',
  line_price: 'line',
  line_quantity: 'line',
  line_total: 'line',
}

export const OVERSIGHT_FIELD_TYPE_VALUES = ['entity', 'text', 'money', 'number', 'date'] as const
type OversightFieldType = (typeof OVERSIGHT_FIELD_TYPE_VALUES)[number]

export const OVERSIGHT_FIELD_TYPE: Readonly<Record<OversightRuleField, OversightFieldType>> = {
  source: 'entity',
  vendor: 'entity',
  company: 'entity',
  external_invoice_id: 'text',
  uploaded_date: 'date',
  invoice_date: 'date',
  subtotal: 'money',
  tax: 'money',
  shipping: 'money',
  prior_balance: 'money',
  total: 'money',
  line_description: 'text',
  line_account: 'entity',
  line_department: 'entity',
  line_code: 'entity',
  line_price: 'money',
  line_quantity: 'number',
  line_total: 'money',
}

export const OVERSIGHT_RULE_OPERATOR = {
  is_any_of: 'is_any_of',
  is_not_any_of: 'is_not_any_of',
  contains: 'contains',
  does_not_contain: 'does_not_contain',
  is: 'is',
  is_greater_than: 'is_greater_than',
  is_at_least: 'is_at_least',
  is_less_than: 'is_less_than',
  is_at_most: 'is_at_most',
  is_equal_to: 'is_equal_to',
  is_before: 'is_before',
  is_after: 'is_after',
  is_on: 'is_on',
} as const
export type OversightRuleOperator =
  (typeof OVERSIGHT_RULE_OPERATOR)[keyof typeof OVERSIGHT_RULE_OPERATOR]

export const OVERSIGHT_RULE_OPERATORS: readonly OversightRuleOperator[] =
  Object.values(OVERSIGHT_RULE_OPERATOR)

/**
 * The words the finished SENTENCE uses.
 *
 * `is at least` / `is at most` rather than "is greater than or equal to": these
 * have to read inside a sentence, and a threshold written as "total is greater
 * than $1,000" quietly lets $1,000.00 through, which is almost never what the
 * person writing that threshold meant.
 */
export const OVERSIGHT_OPERATOR_CLAUSE: Readonly<Record<OversightRuleOperator, string>> = {
  is_any_of: 'is',
  is_not_any_of: 'is not',
  contains: 'contains',
  does_not_contain: 'does not contain',
  is: 'is',
  is_greater_than: 'is greater than',
  is_at_least: 'is at least',
  is_less_than: 'is less than',
  is_at_most: 'is at most',
  is_equal_to: 'is equal to',
  is_before: 'is before',
  is_after: 'is after',
  is_on: 'is on',
}

/**
 * The label the operator DROPDOWN shows. It differs from the sentence clause for
 * the two entity operators alone: a picker reading "is" beside a multi-select
 * hides that several values are allowed, while the finished sentence reads
 * "company is A or B" and needs no plural.
 */
export const OVERSIGHT_OPERATOR_LABEL: Readonly<Record<OversightRuleOperator, string>> = {
  ...OVERSIGHT_OPERATOR_CLAUSE,
  is_any_of: 'is any of',
  is_not_any_of: 'is not any of',
}

export const OPERATORS_BY_TYPE: Readonly<
  Record<OversightFieldType, readonly OversightRuleOperator[]>
> = {
  entity: ['is_any_of', 'is_not_any_of'],
  text: ['contains', 'does_not_contain', 'is'],
  money: ['is_greater_than', 'is_at_least', 'is_less_than', 'is_at_most', 'is_equal_to'],
  number: ['is_greater_than', 'is_at_least', 'is_less_than', 'is_at_most', 'is_equal_to'],
  date: ['is_before', 'is_after', 'is_on'],
}

/** Offered at INVOICE scope only. See `OVERSIGHT_RULE_SCOPE` above for why. */
const NEGATIVE_OVERSIGHT_OPERATORS: readonly OversightRuleOperator[] = [
  OVERSIGHT_RULE_OPERATOR.is_not_any_of,
  OVERSIGHT_RULE_OPERATOR.does_not_contain,
]

/** The operators a field may take at a given scope. The ONE place that rule
    lives: the editor reads it to build its dropdown and the route reads it to
    refuse a write, so a control that offered too much could not be saved. */
export function operatorsFor(
  field: OversightRuleField,
  scope: OversightRuleScope,
): readonly OversightRuleOperator[] {
  const all = OPERATORS_BY_TYPE[OVERSIGHT_FIELD_TYPE[field]]
  if (!isLineScope(scope)) return all
  return all.filter((operator) => !NEGATIVE_OVERSIGHT_OPERATORS.includes(operator))
}

/** Whether a field may be named at a scope. `line_total` is not a fact about an
    invoice header, and `total` is not a fact about one line. */
export function fieldAllowedAtScope(field: OversightRuleField, scope: OversightRuleScope): boolean {
  return OVERSIGHT_FIELD_SCOPE[field] === (isLineScope(scope) ? 'line' : 'invoice')
}

/** Abuse and resource guards (`docs/security.md` section 3.7), enforced server-side. */
export const MAX_OVERSIGHT_CONDITIONS_PER_RULE = 10
/* No cap on how many routing rules may be active (owner, 2026-10-12 — D-350
   amended: "Just get rid of the limit"). `MAX_ACTIVE_OVERSIGHT_RULES` (200) is
   gone with it. */
/**
 * How a view session came to be recorded (`view_sessions.kind`).
 *
 * `view` is one open -> close of the invoice detail modal, timed. `bulk_action`
 * is one invoice inside a bulk decision, which is never opened and so is always
 * zero seconds. The second value exists so that bulk work reads as fast rather
 * than as absent: twenty invoices approved together are twenty near-zero
 * touches, and leaving them out would make the cheapest path through the tool
 * look like a hole in the measure.
 */
export const VIEW_SESSION_KIND = {
  view: 'view',
  bulkAction: 'bulk_action',
} as const
export type ViewSessionKind = (typeof VIEW_SESSION_KIND)[keyof typeof VIEW_SESSION_KIND]

export const VIEW_SESSION_KINDS: readonly ViewSessionKind[] = [
  VIEW_SESSION_KIND.view,
  VIEW_SESSION_KIND.bulkAction,
]

/**
 * PAPER OR ELECTRONIC (owner, 2026-10-19 — D-378): "I am thinking of
 * 'electronic' as any proper digital document, handled directly, while 'paper'
 * is anything that took material form, so it could be a scan or a photo of
 * something physical, even if the subject matter is something that was once
 * electronic but ultimately printed." Stored on `invoices.document_form`,
 * pass 1's best guess per document (`backend/prompts/pass1-extraction.md`),
 * unless an uploader rule's format default (`sender_format_defaults`, D-379)
 * sets it.
 */
export const DOCUMENT_FORM = {
  Paper: 'Paper',
  Electronic: 'Electronic',
} as const
export type DocumentForm = (typeof DOCUMENT_FORM)[keyof typeof DOCUMENT_FORM]
